import type {
  AiLevel,
  GameAction,
  GameState,
  PlayerState,
  ScoreColumn,
  VariantDef,
} from './types';
import {
  boxOptions,
  currentPlayer,
  diceLetters,
  diceValues,
  rollsAllowed,
  variantOf,
  type BoxOption,
} from './reducer';
import { columnTotals } from './scorecard';
import { counts, isAllSame } from './dice';
import { hasDictionary, wordValue, wordsFromKey } from './words';

/*
 * Computer opponent.
 *
 * Numeric variants: every hold subset of the current dice is evaluated by
 * exact expectation over reroll outcomes. Hands are multisets encoded as an
 * 18-bit integer (3 bits per face), so combining "kept + rerolled" is an
 * integer add and memo tables are flat typed arrays. Leaf value of a hand is
 * the best legal box's projected gain (score, bonuses, column multiplier,
 * streak) minus a "regret" term: the potential the box would have had later.
 *
 *   final(hand, r)  = value if scored now, having used r rolls
 *   roll1(hand, r)  = max(final, best hold with one roll left)
 *   roll2(hand, r)  = max(final, best hold whose leaves are roll1)
 *
 * hard = full depth (up to 2 rolls of lookahead), normal = one roll of
 * lookahead, easy = greedy with a dash of randomness. All memo tables live
 * inside one decision; nothing is cached across calls.
 */

/* ---------- multiset encoding and reroll outcome tables ---------- */

const FACES = 6;
const BITS = 3;
const ID_SPACE = 1 << (BITS * FACES);

interface Outcome {
  id: number;
  p: number;
}

function faceShift(face: number): number {
  return BITS * (face - 1);
}

function decodeValues(id: number): number[] {
  const out: number[] = [];
  for (let f = 1; f <= FACES; f++) {
    const c = (id >> faceShift(f)) & 7;
    for (let i = 0; i < c; i++) out.push(f);
  }
  return out;
}

function factorial(n: number): number {
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}

/** All multisets of `n` dice with their probability, as a static table. */
const OUTCOMES: Outcome[][] = [];
function outcomesFor(n: number): Outcome[] {
  const cached = OUTCOMES[n];
  if (cached) return cached;
  const list: Outcome[] = [];
  const total = Math.pow(FACES, n);
  const rec = (face: number, left: number, id: number, denom: number) => {
    if (face > FACES) {
      if (left === 0) list.push({ id, p: factorial(n) / denom / total });
      return;
    }
    if (face === FACES) {
      rec(face + 1, 0, id + (left << faceShift(face)), denom * factorial(left));
      return;
    }
    for (let c = 0; c <= left; c++) {
      rec(
        face + 1,
        left - c,
        id + (c << faceShift(face)),
        denom * factorial(c)
      );
    }
  };
  rec(1, n, 0, 1);
  OUTCOMES[n] = list;
  return list;
}

/** Distinct kept sub-multisets of a hand (id + how many dice were released). */
function keptSubsets(values: number[]): { id: number; released: number }[] {
  const n = values.length;
  const seen = new Set<number>();
  const out: { id: number; released: number }[] = [];
  for (let mask = 0; mask < 1 << n; mask++) {
    let id = 0;
    let kept = 0;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        id += 1 << faceShift(values[i]!);
        kept++;
      }
    }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, released: n - kept });
  }
  return out;
}

/* ---------- per-box targeted statistics (static per variant) ---------- */

interface BoxStats {
  /** Expected score when a whole turn is spent chasing only this box. */
  expected: number;
  /** Probability that turn ends with a non-zero score. */
  hitRate: number;
}

const boxStatsCache = new Map<string, BoxStats>();

/** Heuristic weights, tuned on 200-game Classic samples (hard ~246, normal ~240). */
const AI_TUNING = {
  /** Scale on the opportunistic (rest-of-game) value of a rare box. */
  regret: 0.85,
  /** Extra value of keeping the five-of-a-kind box open early. */
  fiveKindExtra: 6,
  /** Scale on upper-box regret. */
  upperPot: 1.0,
  /** Logistic width for the upper-bonus probability: kBase + kPerBox x open boxes. */
  kBase: 3,
  kPerBox: 2,
};

/**
 * Single-box dynamic program over the variant's rolls: value of every hand
 * with k rolls left = max(score now, best hold's expectation). Static per
 * variant (no game state involved), so it is cached across calls.
 */
function boxStats(variant: VariantDef, categoryId: string): BoxStats {
  const key = `${variant.id}:${categoryId}`;
  const cached = boxStatsCache.get(key);
  if (cached) return cached;
  const category = variant.categories.find(c => c.id === categoryId);
  if (!category) return { expected: 0, hitRate: 0 };

  const column: ScoreColumn = { boxes: {}, fiveKindBonuses: 0 };
  for (const c of variant.categories) column.boxes[c.id] = null;
  if (variant.id === 'generala') column.boxes.generala = 50;
  const card = { cols: [column] };
  const hands = outcomesFor(variant.diceCount);

  let value = new Float64Array(ID_SPACE);
  let hit = new Float64Array(ID_SPACE);
  for (const h of hands) {
    const sc = category.score({
      dice: decodeValues(h.id),
      rollsUsed: variant.maxRolls,
      column,
      card,
      variant,
      joker: false,
    });
    value[h.id] = sc;
    hit[h.id] = sc > 0 ? 1 : 0;
  }

  for (let stage = 1; stage < variant.maxRolls; stage++) {
    const nextValue = new Float64Array(ID_SPACE);
    const nextHit = new Float64Array(ID_SPACE);
    for (const h of hands) {
      let bestEv = value[h.id] ?? 0;
      let bestHit = hit[h.id] ?? 0;
      for (const kept of keptSubsets(decodeValues(h.id))) {
        if (kept.released === 0) continue;
        let ev = 0;
        let ph = 0;
        for (const o of outcomesFor(kept.released)) {
          const id = kept.id + o.id;
          ev += o.p * (value[id] ?? 0);
          ph += o.p * (hit[id] ?? 0);
        }
        if (ev > bestEv + 1e-12) {
          bestEv = ev;
          bestHit = ph;
        }
      }
      nextValue[h.id] = bestEv;
      nextHit[h.id] = bestHit;
    }
    value = nextValue;
    hit = nextHit;
  }

  let expected = 0;
  let hitRate = 0;
  for (const h of hands) {
    expected += h.p * (value[h.id] ?? 0);
    hitRate += h.p * (hit[h.id] ?? 0);
  }
  const stats = { expected, hitRate };
  boxStatsCache.set(key, stats);
  return stats;
}

/* ---------- per-decision evaluation context ---------- */

interface ColumnInfo {
  column: ScoreColumn;
  multiplier: number;
  upperTotal: number;
  /** Upper bonus rule, or null when the variant has none. */
  bonus: { threshold: number; bonus: number } | null;
  /** Bonus already earned on this column. */
  bonusEarned: boolean;
  /** Sum over filled upper boxes of (score - 3 x face). */
  surplus: number;
  /** Open upper faces (1..6). */
  openUpper: number[];
  /** Regret value per open box id (upper and lower). */
  potential: Record<string, number>;
  /** Bonus earned right now for a five-of-a-kind hand, regardless of box. */
  fiveKindBonusNow: number;
}

interface Ctx {
  state: GameState;
  variant: VariantDef;
  player: PlayerState;
  columns: ColumnInfo[];
  maxRolls: number;
  finalMemo: Map<number, Float64Array>;
  roll1Memo: Map<number, Float64Array>;
}

function memoTable(map: Map<number, Float64Array>, r: number): Float64Array {
  let t = map.get(r);
  if (!t) {
    t = new Float64Array(ID_SPACE);
    t.fill(Number.NaN);
    map.set(r, t);
  }
  return t;
}

function bonusThreshold(
  variant: VariantDef
): { threshold: number; bonus: number } | null {
  for (let t = 1; t <= 220; t++) {
    const b = variant.upperBonus(t);
    if (b > 0) return { threshold: t, bonus: b };
  }
  return null;
}

function facesOfUpper(id: string): number {
  return Number(id.slice(1)) || 0;
}

function buildColumnInfo(
  state: GameState,
  variant: VariantDef,
  column: ScoreColumn,
  col: number,
  turnsLeft: number
): ColumnInfo {
  const totals = columnTotals(column, variant, col);
  const upperSet = new Set(variant.upperCategoryIds);

  const bonusRule = bonusThreshold(variant);
  let surplus = 0;
  const openUpper: number[] = [];
  for (const id of variant.upperCategoryIds) {
    const v = column.boxes[id];
    const face = facesOfUpper(id);
    if (v == null) openUpper.push(face);
    else surplus += v - 3 * face;
  }

  // Potential of each open lower box: what keeping it open is worth over the
  // rest of the game. Boxes that cannot score on this column right now (e.g.
  // Double Generala before Generala) are free dumps.
  const potential: Record<string, number> = {};
  const lower = variant.categories.filter(c => column.boxes[c.id] == null);
  if (lower.length > 0) {
    const card = { cols: state.players[state.turn]?.card.cols ?? [column] };
    const maxNow: Record<string, number> = {};
    for (const c of lower) maxNow[c.id] = 0;
    for (const o of outcomesFor(variant.diceCount)) {
      const ctx = {
        dice: decodeValues(o.id),
        rollsUsed: variant.maxRolls,
        column,
        card,
        variant,
        joker: false,
      };
      for (const c of lower) {
        const sc = c.score(ctx);
        if (sc > (maxNow[c.id] ?? 0)) maxNow[c.id] = sc;
      }
    }
    for (const c of lower) {
      if ((maxNow[c.id] ?? 0) <= 0) {
        potential[c.id] = 0;
        continue;
      }
      const { expected, hitRate } = boxStats(variant, c.id);
      let p = expected;
      if (hitRate > 0) {
        const opportunistic = Math.min(
          expected / hitRate,
          expected * turnsLeft
        );
        p = Math.max(expected, AI_TUNING.regret * opportunistic);
      }
      if (variant.fiveKind?.categoryId === c.id && turnsLeft > 5)
        p += AI_TUNING.fiveKindExtra;
      if (upperSet.has(c.id)) p *= AI_TUNING.upperPot;
      potential[c.id] = p;
    }
  }

  let fiveKindBonusNow = 0;
  if (variant.fiveKind) {
    const held = column.boxes[variant.fiveKind.categoryId];
    if (held != null && held > 0) fiveKindBonusNow = variant.fiveKind.bonus;
  }

  return {
    column,
    multiplier: variant.columns[col] ?? 1,
    upperTotal: totals.upper,
    bonus: bonusRule,
    bonusEarned: totals.upperBonus > 0,
    surplus,
    openUpper,
    potential,
    fiveKindBonusNow,
  };
}

function buildCtx(state: GameState): Ctx {
  const variant = variantOf(state);
  const player = currentPlayer(state);
  let turnsLeft = 0;
  for (const c of player.card.cols)
    for (const v of Object.values(c.boxes)) if (v == null) turnsLeft++;
  const columns = player.card.cols.map((c, i) =>
    buildColumnInfo(state, variant, c, i, turnsLeft)
  );
  return {
    state,
    variant,
    player,
    columns,
    maxRolls: rollsAllowed(state),
    finalMemo: new Map(),
    roll1Memo: new Map(),
  };
}

/* ---------- hand valuation ---------- */

interface Valued {
  option: BoxOption;
  gain: number;
  adj: number;
}

function withDice(
  state: GameState,
  values: number[],
  rollsUsed: number
): GameState {
  return {
    ...state,
    rollsUsed,
    dice: values.map(value => ({ value, held: false })),
  };
}

/**
 * Rough probability of finishing the upper section at or above the bonus
 * threshold given the current surplus over par (3 of each face). The
 * logistic width shrinks as fewer upper boxes remain.
 */
function bonusProbability(
  info: ColumnInfo,
  surplus: number,
  open: number[],
  upperTotal: number
): number {
  if (!info.bonus) return 0;
  if (upperTotal >= info.bonus.threshold) return 1;
  let reach = upperTotal;
  for (const f of open) reach += 5 * f;
  if (reach < info.bonus.threshold) return 0;
  const k = AI_TUNING.kBase + AI_TUNING.kPerBox * open.length;
  return 1 / (1 + Math.exp(-surplus / k));
}

/** Project every legal box for a hand: real point gain and heuristic-adjusted value. */
function valueOptions(
  ctx: Ctx,
  values: number[],
  rollsUsed: number,
  word?: string
): Valued[] {
  const { variant } = ctx;
  const state = withDice(ctx.state, values, rollsUsed);
  const options = boxOptions(state, word);
  const upperSet = new Set(variant.upperCategoryIds);
  const allSame = isAllSame(values);
  const stacks = variant.streak
    ? Math.min(ctx.player.streak, variant.streak.maxStacks)
    : 0;
  const streakMult = variant.streak ? 1 + stacks * variant.streak.step : 1;
  const out: Valued[] = [];

  for (const option of options) {
    if (!option.legal) continue;
    const info = ctx.columns[option.col];
    if (!info) continue;
    let raw = option.score;
    if (variant.streak && raw > 0) raw = Math.round(raw * streakMult);
    let gain = raw;
    const isUpper = upperSet.has(option.categoryId);
    let bonusDelta = 0;
    if (isUpper) {
      bonusDelta =
        variant.upperBonus(info.upperTotal + raw) -
        variant.upperBonus(info.upperTotal);
      gain += bonusDelta;
    }
    if (allSame) gain += info.fiveKindBonusNow;
    gain *= info.multiplier;

    let adj = gain - (info.potential[option.categoryId] ?? 0) * info.multiplier;
    if (isUpper && info.bonus && !info.bonusEarned) {
      // Replace the realized bonus jump with the change in bonus probability.
      const face = facesOfUpper(option.categoryId);
      const pBefore = bonusProbability(
        info,
        info.surplus,
        info.openUpper,
        info.upperTotal
      );
      const openAfter = info.openUpper.filter(f => f !== face);
      const pAfter = bonusProbability(
        info,
        info.surplus + raw - 3 * face,
        openAfter,
        info.upperTotal + raw
      );
      adj +=
        (info.bonus.bonus * (pAfter - pBefore) - bonusDelta) * info.multiplier;
    }
    if (variant.streak && raw === 0) adj -= 5 + 2 * stacks;
    if (
      variant.servedInstantWin === option.categoryId &&
      rollsUsed === 1 &&
      raw > 0
    )
      adj += 1000;
    out.push({ option, gain, adj });
  }
  return out;
}

function bestValued(list: Valued[]): Valued | null {
  let best: Valued | null = null;
  for (const v of list) if (!best || v.adj > best.adj) best = v;
  return best;
}

function finalValue(ctx: Ctx, id: number, r: number): number {
  const table = memoTable(ctx.finalMemo, r);
  const hit = table[id];
  if (hit !== undefined && !Number.isNaN(hit)) return hit;
  const best = bestValued(valueOptions(ctx, decodeValues(id), r));
  const v = best ? best.adj : -1e6;
  table[id] = v;
  return v;
}

/** Best achievable value from `id` with exactly one roll remaining, r rolls used. */
function roll1Value(ctx: Ctx, id: number, r: number): number {
  const table = memoTable(ctx.roll1Memo, r);
  const hit = table[id];
  if (hit !== undefined && !Number.isNaN(hit)) return hit;
  let best = finalValue(ctx, id, r);
  const values = decodeValues(id);
  const final = memoTable(ctx.finalMemo, r + 1);
  for (const kept of keptSubsets(values)) {
    if (kept.released === 0) continue;
    let ev = 0;
    for (const o of outcomesFor(kept.released)) {
      const hid = kept.id + o.id;
      let fv = final[hid];
      if (fv === undefined || Number.isNaN(fv))
        fv = finalValue(ctx, hid, r + 1);
      ev += o.p * fv;
    }
    if (ev > best) best = ev;
  }
  table[id] = best;
  return best;
}

interface HoldPlan {
  /** Expected value of the best hold-and-roll plan. */
  ev: number;
  /** Which dice (by position) to hold. */
  held: boolean[];
}

/** Best hold set from the current dice at the given lookahead depth (1 or 2). */
function bestHold(
  ctx: Ctx,
  values: number[],
  r: number,
  depth: number
): HoldPlan | null {
  const n = values.length;
  let best: HoldPlan | null = null;
  const seen = new Set<number>();
  for (let mask = 0; mask < (1 << n) - 1; mask++) {
    let id = 0;
    let released = n;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        id += 1 << faceShift(values[i]!);
        released--;
      }
    }
    if (seen.has(id)) continue;
    seen.add(id);
    let ev = 0;
    for (const o of outcomesFor(released)) {
      const hid = id + o.id;
      ev +=
        o.p *
        (depth >= 2
          ? roll1Value(ctx, hid, r + 1)
          : finalValue(ctx, hid, r + 1));
    }
    if (!best || ev > best.ev + 1e-9) {
      const held: boolean[] = [];
      for (let i = 0; i < n; i++) held.push((mask & (1 << i)) !== 0);
      best = { ev, held };
    }
  }
  return best;
}

function sameHolds(state: GameState, held: boolean[]): boolean {
  return state.dice.every((d, i) => d.held === held[i]);
}

function holdOrRoll(state: GameState, held: boolean[]): GameAction {
  if (sameHolds(state, held)) return { type: 'roll' };
  return { type: 'setHolds', held };
}

function scoreAction(v: Valued): GameAction {
  return { type: 'score', categoryId: v.option.categoryId, col: v.option.col };
}

/* ---------- Power Play ---------- */

function powerAction(
  ctx: Ctx,
  values: number[],
  r: number,
  current: Valued | null
): GameAction | null {
  const { variant, player, state } = ctx;
  if (!variant.powers) return null;
  const base = current ? current.adj : -1e6;
  const MIN_GAIN = 12;

  let best: { adj: number; action: GameAction } | null = null;
  const consider = (next: number[], action: GameAction) => {
    const v = bestValued(valueOptions(ctx, next, r));
    if (!v) return;
    if (v.adj - base < MIN_GAIN) return;
    if (!best || v.adj > best.adj) best = { adj: v.adj, action };
  };

  if ((player.powers.flip ?? 0) > 0) {
    values.forEach((v, i) => {
      const next = [...values];
      next[i] = 7 - v;
      consider(next, { type: 'usePower', power: 'flip', index: i });
    });
  }
  if ((player.powers.nudge ?? 0) > 0) {
    values.forEach((v, i) => {
      for (const delta of [1, -1] as const) {
        const nv = v + delta;
        if (nv < 1 || nv > 6) continue;
        const next = [...values];
        next[i] = nv;
        consider(next, { type: 'usePower', power: 'nudge', index: i, delta });
      }
    });
  }
  if (best) return (best as { action: GameAction }).action;

  // Extra roll: only when out of rolls and the hand is poor but improvable.
  if (
    (player.powers.reroll ?? 0) > 0 &&
    state.rollsUsed >= rollsAllowed(state)
  ) {
    const gainNow = current ? current.gain : 0;
    if (gainNow < 14) {
      const plan = bestHold(ctx, values, r, 1);
      if (plan && plan.ev > base + 6)
        return { type: 'usePower', power: 'reroll' };
    }
  }
  return null;
}

/* ---------- numeric decision ---------- */

function decideNumeric(
  state: GameState,
  level: AiLevel,
  random: () => number
): GameAction {
  if (level === 'easy') return decideEasy(state, random);

  const ctx = buildCtx(state);
  const values = diceValues(state);
  const r = state.rollsUsed;
  const rollsLeft = rollsAllowed(state) - r;
  const now = bestValued(valueOptions(ctx, values, r));

  if (rollsLeft <= 0) {
    const power = powerAction(ctx, values, r, now);
    if (power) return power;
    if (now) return scoreAction(now);
    return fallbackScore(state);
  }

  const depth = level === 'hard' ? Math.min(rollsLeft, 2) : 1;
  const plan = bestHold(ctx, values, r, depth);
  const nowValue = now ? now.adj : -1e6;
  if (plan && plan.ev > nowValue + 1e-6) return holdOrRoll(state, plan.held);
  if (now) return scoreAction(now);
  return { type: 'roll' };
}

function decideEasy(state: GameState, random: () => number): GameAction {
  const values = diceValues(state);
  const rollsLeft = rollsAllowed(state) - state.rollsUsed;
  const legal = boxOptions(state).filter(o => o.legal);
  legal.sort((a, b) => b.score - a.score);
  const best = legal[0];
  const anyHeld = state.dice.some(d => d.held);

  if (rollsLeft > 0 && !anyHeld && random() < 0.3) {
    const held = values.map(() => random() < 0.5);
    return holdOrRoll(state, held);
  }
  if (rollsLeft <= 0 || (best && best.score >= 25) || !best) {
    if (best)
      return scoreAction({ option: best, gain: best.score, adj: best.score });
    return fallbackScore(state);
  }
  // Hold the most common face (if it appears at least twice), plus whatever is already held.
  const c = counts(values);
  let mode = 0;
  for (let f = 6; f >= 1; f--)
    if ((c[f] ?? 0) >= 2 && (c[f] ?? 0) > (c[mode] ?? 0)) mode = f;
  const held = state.dice.map(
    (d, i) => d.held || (mode > 0 && values[i] === mode)
  );
  return holdOrRoll(state, held);
}

/** Last resort: any legal box (or roll if somehow none). */
function fallbackScore(state: GameState): GameAction {
  const legal = boxOptions(state).filter(o => o.legal);
  const pick = legal[0];
  if (pick)
    return { type: 'score', categoryId: pick.categoryId, col: pick.col };
  return { type: 'roll' };
}

/* ---------- word variant ---------- */

const LENGTH_BONUS: Record<number, number> = { 4: 5, 5: 10, 6: 20, 7: 50 };
const WORD_POTENTIAL: Record<string, number> = {
  w3: 4,
  w4: 9,
  w5: 15,
  w6: 24,
  w7: 40,
  vowelly: 12,
  doubled: 12,
  pricey: 14,
  anyWord: 8,
};
const KEEP_LETTERS = 'AEIOSTRNL';

function wordScore(word: string): number {
  return wordValue(word) + (LENGTH_BONUS[word.length] ?? 0);
}

/** Every distinct word spellable from the letters, best first. */
function candidateWords(letters: string[]): string[] {
  if (!hasDictionary()) return [];
  const n = letters.length;
  const seenKeys = new Set<string>();
  const found = new Set<string>();
  for (let mask = 1; mask < 1 << n; mask++) {
    const picked: string[] = [];
    for (let i = 0; i < n; i++) if (mask & (1 << i)) picked.push(letters[i]!);
    if (picked.length < 3) continue;
    const key = picked.sort().join('');
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    for (const w of wordsFromKey(key)) found.add(w);
  }
  return [...found].sort(
    (a, b) => wordScore(b) - wordScore(a) || a.localeCompare(b)
  );
}

export function bestWord(letters: string[]): string | null {
  const upper = letters.map(l => l.toUpperCase());
  return candidateWords(upper)[0] ?? null;
}

function holdsForWord(letters: string[], word: string): boolean[] {
  const held = letters.map(() => false);
  for (const ch of word) {
    const i = letters.findIndex((l, idx) => l === ch && !held[idx]);
    if (i >= 0) held[i] = true;
  }
  return held;
}

function decideWord(state: GameState, level: AiLevel): GameAction {
  const letters = diceLetters(state) ?? [];
  const rollsLeft = rollsAllowed(state) - state.rollsUsed;
  const words = candidateWords(letters);
  const best = words[0] ?? null;
  const target = level === 'easy' ? 3 : level === 'normal' ? 4 : 5;

  if (rollsLeft > 0 && (!best || (best.length < target && best.length < 7))) {
    let held: boolean[];
    if (best) held = holdsForWord(letters, best);
    else {
      let kept = 0;
      held = letters.map(l => {
        if (kept >= 4 || !KEEP_LETTERS.includes(l)) return false;
        kept++;
        return true;
      });
    }
    return holdOrRoll(state, held);
  }

  const player = currentPlayer(state);
  const column = player.card.cols[0];
  let turnsLeft = 0;
  if (column)
    for (const v of Object.values(column.boxes)) if (v == null) turnsLeft++;
  const decay = Math.min(1, turnsLeft / 5);

  for (const word of words) {
    const options = boxOptions(state, word).filter(o => o.legal && o.score > 0);
    let pick: BoxOption | null = null;
    let pickValue = -Infinity;
    for (const o of options) {
      const value = o.score - (WORD_POTENTIAL[o.categoryId] ?? 0) * decay;
      if (value > pickValue) {
        pickValue = value;
        pick = o;
      }
    }
    if (pick)
      return {
        type: 'score',
        categoryId: pick.categoryId,
        col: pick.col,
        word,
      };
  }

  // Nothing playable: scratch the box with the least future value.
  const open = boxOptions(state).filter(o => o.legal);
  open.sort(
    (a, b) =>
      (WORD_POTENTIAL[a.categoryId] ?? 0) - (WORD_POTENTIAL[b.categoryId] ?? 0)
  );
  const dump = open[0];
  if (dump)
    return { type: 'score', categoryId: dump.categoryId, col: dump.col };
  return { type: 'roll' };
}

/* ---------- entry point ---------- */

/**
 * Decide the next action for the current (AI) player. Always returns a legal
 * action; the driver calls this repeatedly until the turn ends. `random` is a
 * [0,1) PRNG used only by the easy level, so normal/hard are deterministic.
 */
export function chooseAiAction(
  state: GameState,
  level: AiLevel,
  random: () => number
): GameAction {
  if (state.phase !== 'playing') return { type: 'roll' };
  if (state.rollsUsed === 0) return { type: 'roll' };
  const variant = variantOf(state);
  if (variant.letterFaces) return decideWord(state, level);
  return decideNumeric(state, level, random);
}
